import React from 'react'
import { TooltipRenderProps } from 'react-joyride'
import { Paper, Box, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import TourFeedback from '../../../tour/TourFeedback'
import { TutorialStep as ImportedTutorialStep } from '../../../tour/types'

// ===== UTILITY FUNCTIONS =====

/**
 * Maps page context to tour type for feedback submission
 * This determines which type of feedback is being collected
 *
 * @param pageContext - The current page/application context
 * @returns The corresponding tour type for feedback categorization
 */
const mapPageContextToTourType = (
  pageContext?: 'patternBuilder' | 'backtest' | 'alerts'
): 'ALERT' | 'PATTERN' | 'BACKTEST' => {
  switch (pageContext) {
    case 'patternBuilder':
      return 'PATTERN'
    case 'backtest':
      return 'BACKTEST'
    case 'alerts':
      return 'ALERT'
    default:
      return 'PATTERN'
  }
}

// ===== COMPONENT INTERFACE =====

/**
 * Props for the FeedbackStepTooltip component
 * Extends Joyride's TooltipRenderProps with feedback-specific context
 */
interface FeedbackStepTooltipProps extends TooltipRenderProps {
  pageContext?: 'patternBuilder' | 'backtest' | 'alerts'
  usermeta: any
  tradingHouse: any
  onFeedbackSubmitAndProceed: (isLastStep: boolean) => void
}

const FeedbackStepTooltip: React.FC<FeedbackStepTooltipProps> = ({
  step,
  closeProps,
  tooltipProps,
  pageContext,
  usermeta,
  tradingHouse,
  onFeedbackSubmitAndProceed,
  index,
  size,
}) => {
  // Cast step to access custom properties like title
  const currentStepData = step as ImportedTutorialStep

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        minWidth: 320,
        maxWidth: 450,
      }}
      data-tour-element="true"
      {...(tooltipProps as any)}
    >
      {/* Optional Header Section */}
      {currentStepData.title && (
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          {/* Step Title */}
          <Box component="h3" sx={{ m: 0, fontSize: '1.1rem', textAlign: 'center', flexGrow: 1 }}>
            {React.isValidElement(currentStepData.title)
              ? currentStepData.title
              : String(currentStepData.title || '')}
          </Box>

          {/* Close Button */}
          <IconButton
            {...closeProps}
            size="small"
            sx={{ ml: 1 }}
            title="Close tour"
            data-tour-element="true"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Feedback Form Section */}
      <TourFeedback
        tourType={mapPageContextToTourType(pageContext)}
        gcid={usermeta?.gcid}
        tradingHouse={tradingHouse}
        onSubmit={() => onFeedbackSubmitAndProceed(index === size - 1)}
      />
    </Paper>
  )
}

export default FeedbackStepTooltip
